import type React from 'react';
import { Navigate } from 'react-router-dom';

import { usePermissions } from '@/contexts/auth';
import { useGetLocationLicenses } from '@/domain/licenses';

/**
 * Props for the ProtectedRoute component
 */
interface ProtectedRouteProps {
    /** The child components to render if access is granted */
    children?: React.ReactNode;
    /** Array of license regex patterns required to access this route */
    requiredLicenses?: string[];
}

/**
 * A component that protects routes based on user licenses and admin status.
 * - Allows access for Optoscale admins
 * - Checks for required licenses for non-admin users
 * - Redirects to home page if license requirements aren't met
 */
function ProtectedRoute({ children, requiredLicenses = [] }: ProtectedRouteProps) {
    const { isOptoScaleAdmin } = usePermissions();

    // Extract IDs from URL path
    const pathSegments = window.location.pathname.split('/');
    const clientId = Number.parseInt(pathSegments[2]);
    const locationId = Number.parseInt(pathSegments[4]);

    // Fetch license data for the client
    const { data: locationLicenses } = useGetLocationLicenses(clientId);

    /**
     * Checks if the current location has a valid license
     */
    const getLocationLicense = () => {
        return locationLicenses?.find((location) => location.locationId === locationId)?.licenses;
    };

    const locationLicense = getLocationLicense();

    /**
     * Checks if a specific license matches any of the required license patterns
     * @param licenseToCheck The license string to validate
     * @returns boolean indicating if license is valid
     */
    const hasRequiredLicense = (licenseToCheck: string) => {
        return requiredLicenses.some((regexPattern) => {
            try {
                const regex = new RegExp(regexPattern);
                return regex.test(licenseToCheck);
            } catch (error) {
                console.error(`Invalid regex pattern: ${regexPattern}`, error);
                return false;
            }
        });
    };

    // Grant immediate access to Optoscale admins
    if (isOptoScaleAdmin) {
        return children;
    }

    // Check license requirements for non-admins
    if (requiredLicenses.length > 0) {
        const needsLiveStreamLicense = hasRequiredLicense('LIVE_VIDEO_STREAM');
        const hasValidLiveStreamLicense = locationLicense?.liveStreamLicense?.hasValidLicense;

        if (needsLiveStreamLicense && !hasValidLiveStreamLicense) {
            return <Navigate to="/" replace />;
        }
    }

    return children;
}

export default ProtectedRoute;
export { ProtectedRoute };
